import { useEffect, useRef, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InputQuantity from 'components/others/InputQuantity';
import PriceAndPriceSale from 'components/others/PriceAndPriceSale';
import ProductsSelect from '../../../Products/ProductsSelect';
import type { ProductSelectOption } from '../../../Products/types';
import type { SetEditFormValues, SetItemFormType } from '../../types';
import SolarIcon from 'components/icons/SolarIcon';

interface SetFormItemsProps {
    formData: SetEditFormValues;
    onChange: (patch: Partial<SetEditFormValues>) => void;
    disabled?: boolean;
}

const emptyDraftItem = (): SetItemFormType => ({
    productId: 0,
    itemCount: 1,
});

const SetFormItems = ({ formData, onChange, disabled = false }: SetFormItemsProps) => {
    const { t } = useTranslation(['Sets']);
    const quantityRef = useRef<HTMLInputElement>(null);
    const [draftItem, setDraftItem] = useState<SetItemFormType>(emptyDraftItem());
    const [selectedProduct, setSelectedProduct] = useState<ProductSelectOption | null>(null);

    const canAddItem = draftItem.productId > 0 && draftItem.itemCount > 0;

    useEffect(() => {
        if (!draftItem.productId) return;
        quantityRef.current?.focus();
    }, [draftItem.productId]);

    const handleAddItem = () => {
        if (!canAddItem) return;

        const existingIndex = formData.items.findIndex((item) => item.productId === draftItem.productId);

        if (existingIndex >= 0) {
            const nextItems = formData.items.map((item, index) => {
                if (index !== existingIndex) return item;
                return {
                    ...item,
                    itemCount: Math.round((item.itemCount + draftItem.itemCount) * 100) / 100,
                };
            });
            onChange({ items: nextItems });
        } else {
            onChange({
                items: [
                    ...formData.items,
                    {
                        ...draftItem,
                        prodName: selectedProduct?.label,
                        prodArticle: selectedProduct?.article,
                        vendorName: selectedProduct?.vendor,
                        defPrice: Number(selectedProduct?.defPrice || 0),
                        defPriceSale: Number(selectedProduct?.defPriceSale || 0),
                    },
                ],
            });
        }

        setDraftItem(emptyDraftItem());
        setSelectedProduct(null);
    };

    const handleRemoveItem = (productId: number) => {
        onChange({
            items: formData.items.filter((item) => item.productId !== productId),
        });
    };

    const adjustCount = (delta: number) => {
        setDraftItem((prev) => ({
            ...prev,
            itemCount: Math.max(1, Math.round((prev.itemCount + delta) * 100) / 100),
        }));
    };

    const handleCountChange = (rawValue: string) => {
        const parsed = Number(rawValue);
        setDraftItem((prev) => ({
            ...prev,
            itemCount: parsed > 0 ? parsed : 0,
        }));
    };

    return (
        <>
            {!disabled && (
            <>
            <ProductsSelect
                title={t('edit.fields.product')}
                required={false}
                value={draftItem.productId || null}
                onChange={(option) => {
                    setSelectedProduct(option);
                    setDraftItem((prev) => ({
                        ...prev,
                        productId: option?.value ? Number(option.value) : 0,
                    }));
                }}
            />

            <InputQuantity
                ref={quantityRef}
                label={t('edit.fields.itemCount')}
                className="mb-3"
                value={draftItem.itemCount}
                onDown={() => adjustCount(-1)}
                onUp={() => adjustCount(1)}
                onChange={handleCountChange}
                onEnter={handleAddItem}
                inputGroupClassName="SetFormQuantityGroup flex-shrink-0"
                buttonClassName="SetFormQuantityBtn"
                inputClassName="text-center SetFormQuantityInput"
                decreaseAriaLabel={t('edit.decreaseCount')}
                increaseAriaLabel={t('edit.increaseCount')}
                actionsClassName="d-flex gap-1 align-items-stretch SetFormAddRow"
                actions={
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        className="flex-grow-1 SetFormAddBtn"
                        disabled={!canAddItem}
                        onClick={handleAddItem}
                    >
                        <SolarIcon name="plus" className="me-1" />
                        <span className="SetFormAddBtnText">{t('edit.addItem')}</span>
                    </Button>
                }
            />
            </>
            )}

            {formData.items.length > 0 ? (
                <div className="crm-table-wrap">
                    <Table size="sm" className="SetItemsTable mb-0">
                        <thead>
                            <tr className="bg-light">
                                <td className="head-product">{t('edit.fields.product')}</td>
                                <td className="head-price">{t('fields.priceList')}</td>
                                <td className="head-count text-end">{t('edit.fields.itemCount')}</td>
                                {!disabled && <td className="head-action"></td>}
                            </tr>
                        </thead>
                        <tbody>
                            {formData.items.map((item) => {
                                const count = Number(item.itemCount) || 0;

                                return (
                                    <tr key={item.productId}>
                                        <td className="product">
                                            <div className="fw-medium">{item.prodName || `#${item.productId}`}</div>
                                            {item.vendorName ? (
                                                <small className="text-muted">{item.vendorName}</small>
                                            ) : null}
                                        </td>
                                        <td className="price text-truncate">
                                            <PriceAndPriceSale
                                                price={(Number(item.defPrice) || 0) * count}
                                                priceSale={(Number(item.defPriceSale) || 0) * count}
                                            />
                                        </td>
                                        <td className="count text-end">{item.itemCount}</td>
                                        {!disabled && (
                                        <td className="action text-end">
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="text-danger p-0"
                                                onClick={() => handleRemoveItem(item.productId)}
                                            >
                                                <SolarIcon name="trash" />
                                            </Button>
                                        </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <p className="text-muted mb-0">{t('edit.itemsEmpty')}</p>
            )}
        </>
    );
};

export default SetFormItems;
